import React from 'react';
import { useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import AlertComponent from '../../../utility/AlertComponent';
import IAlertType from '../../../../utils/types/props/IAlertType';
import apiClient from '../../../../services/api/apiClient';
import { formatAxiosError } from '../../../../utils/formatAxiosError';
import { IAgent } from '../../../../utils/types/IAgent';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

//#region interfaces

interface ConfigAgentProps {
    agent: IAgent;
    setShowModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

//#endregion

const ConfigAgentModalContent: React.FC<ConfigAgentProps> = ({ agent, setShowModal }) => {
    //#region useState

    const [login, setLogin] = useState<string>(agent.login ?? '');
    const [password, setPassword] = useState<string>('');

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };
    const addAlert = (type: IAlertType, message: string) => {
        setSystemAlerts((prevAlerts) => [...prevAlerts, { type, message }]);
    };

    //#endregion

    const { mutate, isPending: isSubmittingOperation } = useMutation({
        mutationFn: () =>
            apiClient.post('/api/agents/config', {
                agentId: agent.id,
                login: login,
                password: password,
            }),
        onSuccess: (response) => {
            if (response.data.success === true) {
                Swal.fire({
                    text: 'Opération effectuée avec succès.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    setShowModal?.(false);
                });
            } else {
                setSystemAlerts((prevAlerts) => [
                    ...prevAlerts,
                    {
                        type: 'danger',
                        message: "Erreur lors l'enregistrement de l'opération. " + response.data.message,
                    },
                ]);
            }
        },
        onError: (err) => {
            addAlert('danger', formatAxiosError(err)?.message ?? 'Erreur lors de la configuration du compte agent.');
        },
    });

    //#region methods

    const submitForm = () => {
        if (!login || !password) {
            toast('Veuillez remplir tous les champs obligatoires.');
            return;
        }

        mutate();
    };

    //#endregion

    // ---

    return (
        <>
            <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />

            <div>
                <form>
                    <div className="row">
                        <div className="col-md-12 form-group">
                            <label>Nom d'utilisateur *</label>
                            <input type="text" className="form-control" disabled={agent.login != null} value={login} onChange={(e) => setLogin(e.target.value)} />
                        </div>

                        <div className="col-md-12 form-group">
                            <label>Mot de passe *</label>
                            <input type="password" className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} />
                        </div>
                    </div>

                    <div className="text-end mt-3">
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={() => {
                                submitForm();
                            }}
                            disabled={isSubmittingOperation}
                        >
                            {isSubmittingOperation ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    &nbsp; Valider
                                </>
                            ) : (
                                `Valider`
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

const ConfigAgentModal: React.FC<ConfigAgentProps> = ({ agent }) => {
    //#region useState

    // State to control modal visibility
    const [showModal, setShowModal] = useState(false);

    // Functions to toggle the modal

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    //#endregion

    // ---

    return (
        <div>
            <button
                onClick={handleOpenModal}
                style={{
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: '#0066cc',
                    textDecoration: 'underline',
                }}
            >
                Configurer
            </button>

            {/* Modal component */}
            <Modal show={showModal} onHide={handleCloseModal} className="fade" centered scrollable backdrop="static" size="sm">
                <Modal.Header closeButton>
                    <Modal.Title>Configurer Agent</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ConfigAgentModalContent agent={agent} setShowModal={setShowModal} />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ConfigAgentModal;
